import React, { useState } from "react";

import { AlertCircle, Edit, Plus, Trash } from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useSalesConditions } from "~/hooks/useSalesConditions";
import { SalesCondition } from "~/types/client";

// Plain-language explanation of what each "Tipo" controls, shown under the
// select so an admin understands the payment behavior it implies. "Prepago"
// gates the order (must be paid before dispatch); the other two are pay-later
// terms whose plazo is set by the "Días" field below.
const TYPE_DESCRIPTIONS: Record<SalesCondition["type"], string> = {
  prepaid:
    "Pago anticipado: la orden no avanza a despacho hasta que el cliente pague.",
  credit_line:
    "Crédito directo: el cliente paga dentro del plazo en días que definas abajo.",
  external_platform:
    "Crédito respaldado por una plataforma externa (ej. Telecheque, Fincred) con un plazo en días.",
};

const SalesConditionsPage: React.FC = () => {
  const {
    salesConditions,
    loading,
    error,
    addSalesCondition,
    updateCondition,
    removeCondition,
  } = useSalesConditions();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingCondition, setEditingCondition] =
    useState<SalesCondition | null>(null);
  const [deletingConditionId, setDeletingConditionId] = useState<string | null>(
    null
  );

  // Form state
  const [formData, setFormData] = useState<{
    name: string;
    type: "prepaid" | "credit_line" | "external_platform";
    maximumAmount?: number;
    maximumDays?: number;
    externalPlatformName?: string;
    requiresPrepayment?: boolean;
  }>({
    name: "",
    // Default to the most common condition type (credit_line). "prepaid" is the
    // rare exception and would otherwise nudge the form into the prepaid gate.
    type: "credit_line",
    requiresPrepayment: false,
  });

  const resetForm = () => {
    setFormData({
      name: "",
      type: "credit_line",
      requiresPrepayment: false,
    });
    setEditingCondition(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (condition: SalesCondition) => {
    setEditingCondition(condition);
    setFormData({
      name: condition.name,
      type: condition.type,
      maximumAmount: condition.maximumAmount,
      maximumDays: condition.maximumDays,
      externalPlatformName: condition.externalPlatformName,
      requiresPrepayment: condition.requiresPrepayment,
    });
    setIsDialogOpen(true);
  };

  const openDeleteDialog = (conditionId: string) => {
    setDeletingConditionId(conditionId);
    setIsDeleteDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (editingCondition) {
      await updateCondition(editingCondition.id, formData);
    } else {
      await addSalesCondition(formData);
    }
    setIsDialogOpen(false);
    resetForm();
  };

  const handleDelete = async () => {
    if (deletingConditionId) {
      await removeCondition(deletingConditionId);
    }
    setIsDeleteDialogOpen(false);
    setDeletingConditionId(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // For numeric fields, convert to number
    if (name === "maximumAmount" || name === "maximumDays") {
      const numValue = value === "" ? undefined : Number(value);
      setFormData({ ...formData, [name]: numValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleTypeChange = (value: string) => {
    const type = value as "prepaid" | "credit_line" | "external_platform";

    let requiresPrepayment = false;

    switch (type) {
      case "prepaid":
        requiresPrepayment = true;
        break;
      case "credit_line":
        requiresPrepayment = false;
        break;
      case "external_platform":
        requiresPrepayment = false;
        break;
    }

    setFormData({
      ...formData,
      type,
      requiresPrepayment,
      // Reset conditional fields when type changes
      maximumAmount: undefined,
      maximumDays: type === "prepaid" ? 0 : undefined,
      externalPlatformName:
        type === "external_platform" ? "Telecheque" : undefined,
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto py-6">
        <h1 className="mb-6 text-2xl font-bold">Condiciones de Venta</h1>
        <Skeleton className="mb-4 h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <h1 className="mb-6 text-2xl font-bold">Condiciones de Venta</h1>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Ocurrió un error al cargar las condiciones de venta. Por favor,
            intente nuevamente.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="max-w-[85vw] px-6 md:max-w-[70vw]">
      <div className="mb-6 flex flex-col justify-between gap-y-4 md:flex-row md:items-center">
        <h1 className="text-2xl font-bold">Condiciones de Venta</h1>
        <Button onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" /> Nueva Condición
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Detalles</TableHead>
              <TableHead>Anticipo Requerido</TableHead>
              <TableHead className="w-24">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {salesConditions.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="py-4 text-center text-gray-500"
                >
                  No hay condiciones de venta definidas
                </TableCell>
              </TableRow>
            ) : (
              salesConditions.map((condition, index) => (
                <TableRow
                  key={condition.id}
                  style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                  className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                >
                  <TableCell className="font-medium">
                    {condition.name}
                  </TableCell>
                  <TableCell>
                    {condition.type === "prepaid" && "Prepago"}
                    {condition.type === "credit_line" && "Línea de Crédito"}
                    {condition.type === "external_platform" &&
                      "Plataforma Externa"}
                  </TableCell>
                  <TableCell>
                    {/* Credit line details */}
                    {condition.type === "credit_line" && (
                      <div>
                        <p className="text-sm">
                          Monto Máximo: $
                          {condition.maximumAmount?.toLocaleString() || "N/A"}
                        </p>
                        <p className="text-sm">
                          Días: {condition.maximumDays || "N/A"}
                        </p>
                      </div>
                    )}

                    {/* External platform with days */}
                    {condition.type === "external_platform" && (
                      <div>
                        {condition.externalPlatformName && (
                          <p className="text-sm">
                            Plataforma: {condition.externalPlatformName}
                          </p>
                        )}
                        <p className="text-sm">Días: {condition.maximumDays}</p>
                      </div>
                    )}

                    {/* Prepaid with days */}
                    {condition.type === "prepaid" &&
                      condition.maximumDays !== undefined && (
                        <div>
                          <p className="text-sm">
                            {condition.maximumDays === 0
                              ? "Pago anticipado"
                              : `Días: ${condition.maximumDays}`}
                          </p>
                        </div>
                      )}
                  </TableCell>
                  <TableCell>
                    {condition.requiresPrepayment ? "Sí" : "No"}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(condition)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDeleteDialog(condition.id)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCondition
                ? "Editar Condición de Venta"
                : "Nueva Condición de Venta"}
            </DialogTitle>
            <DialogDescription>
              Complete los detalles de la condición de venta a continuación.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Nombre de la condición"
              />
            </div>

            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="type">Tipo</Label>
              <Select value={formData.type} onValueChange={handleTypeChange}>
                <SelectTrigger id="type">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prepaid">Prepago</SelectItem>
                  <SelectItem value="credit_line">Línea de Crédito</SelectItem>
                  <SelectItem value="external_platform">
                    Plataforma Externa
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {TYPE_DESCRIPTIONS[formData.type]}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="requiresPrepayment">
                ¿Requiere pago anticipado?
              </Label>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="requiresPrepayment"
                  checked={formData.requiresPrepayment || false}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      requiresPrepayment: e.target.checked,
                    })
                  }
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label
                  htmlFor="requiresPrepayment"
                  className="text-sm font-normal"
                >
                  La orden debe ser pagada antes de avanzar a despacho
                </Label>
              </div>
            </div>

            {/* For all types, always show payment days field */}
            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="maximumDays">
                {formData.type === "prepaid"
                  ? "Días (0 para pago anticipado)"
                  : "Plazo de Pago (Días)"}
              </Label>
              <Input
                id="maximumDays"
                name="maximumDays"
                type="number"
                min="0"
                max="90"
                value={formData.maximumDays || ""}
                onChange={handleInputChange}
                placeholder={
                  formData.type === "prepaid"
                    ? "0 para anticipado"
                    : "Número de días (1-90)"
                }
              />
            </div>

            {/* For credit line type, also show maximum amount field */}
            {formData.type === "credit_line" && (
              <div className="grid grid-cols-1 gap-2">
                <Label htmlFor="maximumAmount">Monto Máximo ($)</Label>
                <Input
                  id="maximumAmount"
                  name="maximumAmount"
                  type="number"
                  value={formData.maximumAmount || ""}
                  onChange={handleInputChange}
                  placeholder="Monto máximo de crédito"
                />
              </div>
            )}

            {/* Show platform name for external platform type */}
            {formData.type === "external_platform" && (
              <div className="grid grid-cols-1 gap-2">
                <Label htmlFor="externalPlatformName">
                  Nombre de la Plataforma
                </Label>
                <Input
                  id="externalPlatformName"
                  name="externalPlatformName"
                  value={formData.externalPlatformName || "Telecheque"}
                  onChange={handleInputChange}
                  placeholder="Nombre de la plataforma externa"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit}>
              {editingCondition ? "Guardar Cambios" : "Crear Condición"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación</DialogTitle>
            <DialogDescription>
              ¿Está seguro de que desea eliminar esta condición de venta?
              <br />
              <span className="font-medium text-destructive">
                Esta acción no se puede deshacer.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesConditionsPage;
